import { zoomIdentity } from 'd3-zoom';
import type { BehaviorSubject, Observable } from 'rxjs';
import {
    EMPTY,
    catchError,
    defer,
    distinctUntilChanged,
    filter,
    from,
    map,
    switchMap,
    tap,
} from 'rxjs';
import { AllMeasurementsBoundsDocument } from '@/graphql/types';
import {
    getDayDays,
    getHourDays,
    getMinuteDays,
    getMonthDays,
    getWeekDays,
    getYearDays,
    oneDay,
} from '../../../../graph/graph-helpers';
import { getClient } from '../../../../utils/apollo-client';
import warning from '../../../../utils/logger';
import {
    TimeFilter,
    type FilterActionFactory,
    type FilterActionFactoryContext,
    type FilterClickType,
    type DataSetAdapter,
    type GraphAdapter,
} from './types';

type AllMeasurementsBounds = {
    from: number;
    to: number | null;
} | null;

const GO_TO_NOW_PERCENTAGE = 95;

const DAYS_BY_FILTER_TYPE: Record<
    Exclude<TimeFilter, TimeFilter.AllMeasurements | TimeFilter.DatePicker>,
    number
> = {
    [TimeFilter.Year]: getYearDays(),
    [TimeFilter.Month]: getMonthDays(),
    [TimeFilter.Week]: getWeekDays(),
    [TimeFilter.Day]: getDayDays(),
    [TimeFilter.Hour]: getHourDays(),
    [TimeFilter.FiveMinute]: getMinuteDays() * 5,
};

type FilterActionEffectsArgs = {
    dataSet: DataSetAdapter;
    graph: GraphAdapter;
    actionFactory$: Observable<FilterActionFactory>;
    showDateRangePicker$: BehaviorSubject<boolean>;
};

function setAllMeasurementsVisibleRange(
    bounds: NonNullable<AllMeasurementsBounds>,
    dataSet: DataSetAdapter
) {
    const cappedWindowMs = getYearDays() * oneDay;
    const { from: boundsFrom, to: boundsTo } = bounds;
    const visibleMax = boundsTo ?? Date.now();
    const visibleMin = Math.max(boundsFrom, visibleMax - cappedWindowMs);

    dataSet.setVisibleMinMax(visibleMin, visibleMax);
}

function applyToNow(graph: GraphAdapter) {
    const scale =
        getYearDays() /
        ((graph.xAxis.scale.domain()[1].valueOf() - graph.xAxis.scale.domain()[0].valueOf()) /
            oneDay);
    const currentDaysInMillis =
        graph.xAxis.scale.domain()[1].valueOf() - graph.xAxis.scale.domain()[0].valueOf();
    const nowTemp = new Date();

    nowTemp.setMilliseconds(
        nowTemp.getMilliseconds() -
            currentDaysInMillis +
            (currentDaysInMillis / 100) * (100 - GO_TO_NOW_PERCENTAGE)
    );

    graph.zoom
        .zoomTransition()
        .call(
            (...args: unknown[]) => graph.zoom.zoom.transform(...args),
            zoomIdentity.scale(scale).translate(-graph.xAxis.originalScale(nowTemp), 0)
        );
}

export function createToNowLongPress$({ dataSet }: FilterActionFactoryContext): Observable<void> {
    return defer(() => {
        dataSet.dateRange.toggleTimePanning();
        return EMPTY;
    });
}

export function createToNowClick$({
    graph,
    showDateRangePicker$,
}: FilterActionFactoryContext): Observable<void> {
    return defer(() => {
        if (showDateRangePicker$.getValue()) {
            showDateRangePicker$.next(false);
        }

        applyToNow(graph);
        return EMPTY;
    });
}

function createFilterClick$(
    filterType: FilterClickType,
    { graph, showDateRangePicker$ }: FilterActionFactoryContext
) {
    if (filterType === TimeFilter.DatePicker) {
        return defer(() => {
            showDateRangePicker$.next(!showDateRangePicker$.getValue());
            return EMPTY;
        });
    }

    if (showDateRangePicker$.getValue()) {
        showDateRangePicker$.next(false);
    }

    return defer(() => {
        graph.zoom.zoomToScale(DAYS_BY_FILTER_TYPE[filterType]);
        return EMPTY;
    });
}

export function createFilterClickActionFactory(filterType: FilterClickType): FilterActionFactory {
    return (ctx) => createFilterClick$(filterType, ctx);
}

function createAllMeasurementsBounds$({ dataSet, graph }: FilterActionFactoryContext) {
    return dataSet.currentSensor$.pipe(
        map((currentSensorId) => Number(currentSensorId)),
        distinctUntilChanged(),
        switchMap((measuringPointId) => {
            if (!measuringPointId) {
                return EMPTY;
            }

            return from(
                getClient()
                    .query<{ allMeasurementsBounds: AllMeasurementsBounds }>({
                        query: AllMeasurementsBoundsDocument,
                        variables: { measuringPointId },
                        fetchPolicy: 'network-only',
                    })
                    .then(({ data }) => data.allMeasurementsBounds)
            ).pipe(
                filter((bounds): bounds is NonNullable<AllMeasurementsBounds> => bounds !== null),
                tap((bounds) => setAllMeasurementsVisibleRange(bounds, dataSet)),
                map((): void => {}),
                catchError((error) => {
                    warning(error instanceof Error ? error : String(error));
                    graph.zoom.zoomToScale(getYearDays());
                    return EMPTY;
                })
            );
        })
    );
}

export function createAllMeasurementsClick$(ctx: FilterActionFactoryContext): Observable<void> {
    if (ctx.showDateRangePicker$.getValue()) {
        ctx.showDateRangePicker$.next(false);
    }

    return createAllMeasurementsBounds$(ctx);
}

export function filterActionEffects({
    dataSet,
    graph,
    actionFactory$,
    showDateRangePicker$,
}: FilterActionEffectsArgs) {
    const ctx = { dataSet, graph, showDateRangePicker$ };

    return actionFactory$.pipe(switchMap((create$) => create$(ctx)));
}
